import { Observable } from 'rxjs';
import {
  Priority,
  SpecimenType,
  ExamGroup,
  WellPlateConfig,
  ExtractionMethod,
  Cell,
  Plate,
  Index,
  IndexAdapter,
  Configuration,
  ConfigurationParameterType,
  FailureCause,
  ReprocessStatus
} from '../../model/catalog/catalog.model';

/**
 * Abstract repository for catalog operations
 * Provides access to system catalogs (priorities, specimen types, exam groups, well plates, extraction methods)
 * Provides access to system catalogs (priorities, specimen types, exam groups, cells, plates, indexes)
 */
export abstract class CatalogRepository {
  /**
   * Get all priorities ordered by name
   */
  abstract getAllPriorities(): Observable<Priority[]>;

  /**
   * Get all specimen types ordered by name
   */
  abstract getAllSpecimenTypes(): Observable<SpecimenType[]>;

  /**
   * Get all exam groups ordered by name
   */
  abstract getAllExamGroups(): Observable<ExamGroup[]>;

  /**
   * Get active well plate configurations
   */
  abstract getWellPlateConfigs(): Observable<WellPlateConfig[]>;

  /**
   * Get all extraction methods
   */
  abstract getExtractionMethods(): Observable<ExtractionMethod[]>;

  /**
   * Get all cells ordered by name
   */
  abstract getAllCells(): Observable<Cell[]>;

  /**
   * Get all plates ordered by name
   */
  abstract getAllPlates(): Observable<Plate[]>;

  /**
   * Get all indexes ordered by name
   */
  abstract getAllIndexes(): Observable<Index[]>;

  /**
   * Get indexes filtered by plate ID
   * @param plateId - Plate identifier to filter indexes
   */
  abstract getIndexesByPlateId(plateId: string): Observable<Index[]>;

  /**
   * Get index adapters with pagination and filters
   * @param plateId - Plate identifier
   * @param indexId - Index identifier
   */
  abstract getIndexAdapters(
    plateId: string,
    indexId: string
  ): Observable<IndexAdapter[]>;

  /**
   * Get configurations by parameter type
   * @param type - Configuration parameter type (EQUIPMENT, REAGENT_VERSION, INDEX_TYPE)
   */
  abstract getConfigurationsByType(
    type: ConfigurationParameterType
  ): Observable<Configuration[]>;

  /**
   * Get failure causes for sequencing runs.
   * @returns Observable of failure causes for RUN context
   */
  abstract getRunFailureCauses(): Observable<FailureCause[]>;

  /**
   * Get failure causes for run success reprocess.
   * Used in HU44/HU45: Marcar corrida como exitosa / Reprocesos.
   * Supports filtering by technology (parameterType).
   * @param parameterType Optional technology filter (NGS, MICROARREGLOS, MLPA, EXPANSION_TRIPLETAS).
   *                      If not provided, returns causes with GENERAL parameter type.
   * @returns Observable of failure causes for RUN_SUCCESS context
   */
  abstract getRunSuccessCauses(
    parameterType?: string
  ): Observable<FailureCause[]>;

  /**
   * Get failure causes for sequencing specimen reprocess.
   * @returns Observable of failure causes for SEQUENCING context
   */
  abstract getSequencingReprocessCauses(): Observable<FailureCause[]>;

  /**
   * Get all failure causes without context filter.
   * Used in Observations filter to show all available causes.
   * @returns Observable of all failure causes
   */
  abstract getAllCauses(): Observable<FailureCause[]>;

  /**
   * Get all active reprocess statuses ordered by status_order.
   * @returns Observable of reprocess statuses (REPORTADO, EN GESTIÓN, SOLUCIONADO)
   */
  abstract getReprocessStatuses(): Observable<ReprocessStatus[]>;
}
